import React, { HTMLAttributes } from 'react';
import { AlertTriangle, Info, AlertCircle, type LucideIcon } from 'lucide-react';

export interface NoticeProps extends HTMLAttributes<HTMLDivElement> {
  /** info: neutral/tenant-accent guidance. warn: needs attention, not blocking. error: blocking/invalid. */
  variant?: 'info' | 'warn' | 'error';
  /** Hide the leading icon (rare — most notices should keep it). */
  hideIcon?: boolean;
}

const ICONS: Record<NonNullable<NoticeProps['variant']>, LucideIcon> = {
  info: Info,
  warn: AlertTriangle,
  error: AlertCircle,
};

/**
 * Single notice component for the whole app — replaces one-off inline-styled
 * yellow/red boxes with hardcoded hex. Built on the DS `.note` pattern
 * (border + tint) and the semantic status tokens in tokens.css.
 */
export const Notice: React.FC<NoticeProps> = ({
  variant = 'info',
  hideIcon = false,
  children,
  className = '',
  role,
  ...props
}) => {
  const Icon = ICONS[variant];
  const combinedClassName = `notice notice-${variant} ${className}`.trim();

  return (
    <div className={combinedClassName} role={role ?? (variant === 'error' ? 'alert' : 'status')} {...props}>
      {!hideIcon && (
        <span className="notice-icon">
          <Icon size={14} />
        </span>
      )}
      <div>{children}</div>
    </div>
  );
};
